import { formatFlightClock24 } from '@/lib/disruptions/airport-tz';
import type { IOperatorAlertV1 } from './alert.types';

// What one alert SAYS, concrete (brief §1: «Cancelado H2 1821 · EZE → SCL · 22:10»): what
// happened (the kind — copy in `alerts:kind.*`), to what (the flight number; a disruption
// by its REASON — `alerts:reason.*` — never its auto-generated title), where (the route,
// or the airports it touches), when (the departure in the ORIGIN's local time, struck to
// the new one on a delay), and where to go from it. Pure; the components compose it
// through `t()`. No PII ever rides an alert (brief §6 inv. 6).

/** The cases `DisruptionReason` codes the console says in words (`alerts:reason.<code>`). */
export const DISRUPTION_REASONS = [
  'WEATHER',
  'MECHANICAL',
  'STRIKE',
  'ATC',
  'SECURITY',
  'IT_OUTAGE',
  'OTHER',
] as const;
export type DisruptionReasonCode = (typeof DISRUPTION_REASONS)[number];

const isReason = (code: string | undefined): code is DisruptionReasonCode =>
  (DISRUPTION_REASONS as readonly (string | undefined)[]).includes(code);

export interface IAlertView {
  kind: IOperatorAlertV1['kind'];
  /** The flight number (a code); '' for a disruption — it reads by its reason. */
  what: string;
  /** A disruption's reason, when the console can say it; else null («Nueva disrupción»). */
  reason: DisruptionReasonCode | null;
  where: string;
  when: string;
  delay: number | null;
  /** A disruption's flights (a flight alert is one). */
  flights: number | null;
  /**
   * The operator's own words: the title of a disruption an operator declared (`source:
   * OPERATOR`) — a secondary line. A cases auto-title («SKY — MECHANICAL disruption») is
   * never shown.
   */
  note: string | null;
  /** «Operar» — the disruption's operating screen. */
  operate: string | null;
  /** «Ver vuelo» — the flights monitor narrowed to it. */
  flight: string | null;
}

export function alertView(a: IOperatorAlertV1): IAlertView {
  const f = a.flight;
  const d = a.disruption;
  const std = f ? formatFlightClock24(f.std, f.origin) : '';
  const etd = f?.etd && a.kind !== 'FLIGHT_CANCELLED' ? formatFlightClock24(f.etd, f.origin) : '';
  return {
    kind: a.kind,
    what: f?.number ?? '',
    reason: !f && isReason(d?.reason) ? d.reason : null,
    where: f ? `${f.origin} → ${f.destination}` : a.airports.join(', '),
    when: f
      ? etd && etd !== std
        ? `${std} → ${etd}`
        : std
      : formatFlightClock24(a.at, a.airports[0]),
    delay: f?.delayMinutes ?? null,
    flights: f ? null : (d?.flightCount ?? null),
    note: !f && a.source === 'OPERATOR' && d?.title.trim() ? d.title.trim() : null,
    operate: d ? `/disruptions/${encodeURIComponent(d.urn)}/operar` : null,
    flight: f ? `/flights?q=${encodeURIComponent(f.number)}` : null,
  };
}

/** How far apart a flight's news and a disruption can be and still be one story. */
export const PAIR_MS = 2 * 60_000;

/**
 * Is `d` the disruption that `f`'s news opened? The same disruption when the flight names
 * one; otherwise a disruption opened around the same time (`PAIR_MS`) at the flight's
 * origin or destination.
 */
export function openedBy(d: IOperatorAlertV1, f: IOperatorAlertV1): boolean {
  if (d.kind !== 'DISRUPTION_OPENED' || !d.disruption || !f.flight) return false;
  if (f.disruption) return f.disruption.urn === d.disruption.urn;
  if (Math.abs(Date.parse(d.at) - Date.parse(f.at)) > PAIR_MS) return false;
  const ends = [f.flight.origin, f.flight.destination];
  return d.airports.some((iata) => ends.includes(iata));
}

/** What the evident dialog says of a burst (its alerts lead first — `byLead`). */
export interface IAlertGroupView {
  lead: IOperatorAlertV1;
  /**
   * The disruption the lead flight's news opened, read as ONE story with it — «Cancelado
   * H2 300 · SCL → ARI · 05:00» / «Se abrió una disrupción · 1 vuelo» — not «y 1 más».
   */
  paired: IOperatorAlertV1 | null;
  /** «Operar»: the lead's disruption, else the paired one, else the burst's first. */
  operate: string | null;
  /** «y N más» — what the card does not say. */
  more: number;
}

export function groupView(alerts: readonly IOperatorAlertV1[]): IAlertGroupView | null {
  const [lead, ...rest] = alerts;
  if (!lead) return null;
  const paired = lead.flight ? (rest.find((a) => openedBy(a, lead)) ?? null) : null;
  const operate =
    alertView(lead).operate ??
    (paired ? alertView(paired).operate : null) ??
    rest.map((a) => alertView(a).operate).find((to) => to !== null) ??
    null;
  return { lead, paired, operate, more: rest.length - (paired ? 1 : 0) };
}
